/**
 * Minimal Server-Sent Events reader.
 *
 * Deliberately dependency-free: it takes a ReadableStream and returns a value,
 * touching no app state. That keeps it usable from any service and testable on
 * its own, without pulling the Redux store or `window` into the picture.
 *
 * Handles the part that is easy to get wrong — a network chunk can split a frame
 * anywhere, including mid-JSON — which only shows up under real network timing.
 */
/**
 * Read an SSE body to completion.
 *
 * Resolves with whichever terminal frame arrived; it never throws for a
 * server-reported error, so callers decide how to surface it.
 */
export async function readSse(body, opts = {}) {
    const reader = body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let result = null;
    let failure = null;
    for (;;) {
        const { done, value } = await reader.read();
        if (done)
            break;
        buffer += decoder.decode(value, { stream: true });
        // Frames are separated by a blank line; anything partial stays buffered.
        let split;
        while ((split = buffer.indexOf('\n\n')) !== -1) {
            const frame = buffer.slice(0, split);
            buffer = buffer.slice(split + 2);
            // ':' prefixed frames are keep-alive comments — nothing to parse.
            if (frame.startsWith(':'))
                continue;
            // Per the spec a frame may carry several `data:` lines, joined with
            // newlines. Our server's JSON.stringify never emits a raw newline, so
            // today there is always exactly one — but reading only the first line
            // would truncate silently the day that changes.
            const lines = frame.split('\n');
            const event = lines.find((l) => l.startsWith('event:'))?.slice(6).trim();
            const data = lines
                .filter((l) => l.startsWith('data:'))
                .map((l) => l.slice(5).replace(/^ /, ''));
            if (!event || !data.length)
                continue;
            const raw = data.join('\n');
            if (event === 'done') {
                result = JSON.parse(raw);
            }
            else if (event === 'error') {
                failure = JSON.parse(raw)?.message || 'Request failed.';
            }
            else if (opts.onEvent) {
                // Progress frames must never break the read: a listener that
                // throws, or a frame we can't parse, costs that one update only.
                try {
                    opts.onEvent(event, JSON.parse(raw));
                }
                catch {
                    /* ignored on purpose */
                }
            }
        }
    }
    return { result, failure };
}
